/**
 * The date that matters for a work order in its status, with every date of
 * the work order in the tooltip (#3765). The status → date rule lives in
 * @vehiclespace/shared so the list route's `statusDate` sort agrees with it.
 */

import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  listWorkOrderDates,
  resolveStatusDate,
  type WorkOrderDates,
} from '@vehiclespace/shared/types/work-order-status-date';
import { formatDateTimeShort } from '@/utils/locale';

export interface WorkOrderStatusDateCellProps {
  status: string;
  dates: WorkOrderDates;
  /** One line for the mobile card; the table shows the label under the date. */
  compact?: boolean;
}

export function WorkOrderStatusDateCell({ status, dates, compact = false }: WorkOrderStatusDateCellProps) {
  const { t } = useTranslation('workorders');
  const primary = resolveStatusDate(status, dates);

  if (!primary) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const label = t(`statusDate.fields.${primary.field}`);
  const all = listWorkOrderDates(dates);

  const tooltip = (
    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 1.5, rowGap: 0.25 }}>
      {all.map(({ field, value }) => (
        <Box key={field} sx={{ display: 'contents', fontWeight: field === primary.field ? 700 : 400 }}>
          <Box component="dt">{t(`statusDate.fields.${field}`)}</Box>
          <Box component="dd" sx={{ m: 0, whiteSpace: 'nowrap' }}>
            {formatDateTimeShort(value)}
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltip} placement="left" arrow>
      <Box data-testid="work-order-status-date" sx={{ display: 'inline-block' }}>
        {compact ? (
          <Typography variant="caption" color="text.secondary" noWrap>
            {label} {formatDateTimeShort(primary.value)}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" noWrap>
              {formatDateTimeShort(primary.value)}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {label}
            </Typography>
          </>
        )}
      </Box>
    </Tooltip>
  );
}
